import { Clock3 } from "lucide-react";
import { useEffect, useRef, useState } from "react";

type ScheduleTimePickerProps = {
  value: string;
  onChange: (value: string) => void;
};

const hours = Array.from({ length: 24 }, (_, value) => value.toString().padStart(2, "0"));
const minutes = Array.from({ length: 60 }, (_, value) => value.toString().padStart(2, "0"));

export function ScheduleTimePicker({
  value,
  onChange,
}: ScheduleTimePickerProps): React.JSX.Element {
  const [open, setOpen] = useState(false);
  const [hour, setHour] = useState("12");
  const [minute, setMinute] = useState("00");
  const rootRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const hourRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false);
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.preventDefault();
      setOpen(false);
      triggerRef.current?.focus();
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape);
    };
  }, [open]);

  const closeAndFocus = () => {
    setOpen(false);
    window.setTimeout(() => triggerRef.current?.focus(), 0);
  };
  const setTime = () => {
    onChange(`${hour}:${minute}`);
    closeAndFocus();
  };
  const clearTime = () => {
    onChange("");
    closeAndFocus();
  };

  return (
    <div ref={rootRef} className="schedule-time-picker">
      <button
        ref={triggerRef}
        type="button"
        className="schedule-toolbar-field schedule-time-field"
        aria-label="Time Optional"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => {
          if (open) {
            setOpen(false);
            return;
          }
          const parsed = parseTime(value);
          setHour(parsed?.hour ?? "12");
          setMinute(parsed?.minute ?? "00");
          setOpen(true);
          window.setTimeout(() => hourRef.current?.focus(), 0);
        }}
      >
        <span className="schedule-toolbar-value">
          <span className={value ? undefined : "is-placeholder"}>{value || "Any time"}</span>
          <Clock3 size={16} aria-hidden="true" />
        </span>
      </button>
      {open ? (
        <div className="schedule-time-popover" role="dialog" aria-label="Choose time">
          <div className="schedule-time-selectors">
            <select
              ref={hourRef}
              aria-label="Hour"
              value={hour}
              onChange={(e) => setHour(e.target.value)}
            >
              {hours.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
            <span aria-hidden="true">:</span>
            <select aria-label="Minute" value={minute} onChange={(e) => setMinute(e.target.value)}>
              {minutes.map((option) => (
                <option key={option}>{option}</option>
              ))}
            </select>
          </div>
          <div className="schedule-time-actions">
            <button
              type="button"
              className="soft-button schedule-time-clear"
              onPointerDown={(event) => {
                event.preventDefault();
                clearTime();
              }}
              onClick={clearTime}
            >
              Any time
            </button>
            <button
              type="button"
              className="primary-button schedule-time-confirm"
              onPointerDown={(event) => {
                event.preventDefault();
                setTime();
              }}
              onClick={setTime}
            >
              Set time
            </button>
          </div>
        </div>
      ) : null}
    </div>
  );
}

function parseTime(value: string): { hour: string; minute: string } | null {
  const match = /^([01]\d|2[0-3]):([0-5]\d)$/.exec(value);
  return match?.[1] && match[2] ? { hour: match[1], minute: match[2] } : null;
}
